import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { sayQuote } from '../../lib/quick';
import { sayWhat } from '../../lib/models';
import { saySeconds, useElapsed } from '../../lib/waiting';
import { ModelPicker } from '../ModelPicker';
import { OpenInPro, QuoteLine, SendButton, Stage, WatchNote, useAssetUrl, useWatchAsset } from './parts';
import { useBench } from './useBench';
import type { TabProps } from './Quick';

// The Music tab: pick a model, describe the bed, press, hear it.
//
// The same call as the Audio Lab's music bench, with the price on the
// button and the bed playable on the stage when it lands. A bed is thirty
// seconds on whichever model is picked (204): the picker shows each model's
// price for one. "Keep out" is the negative prompt in the creator's words —
// drums, vocals — because the thing a bed most often gets wrong is the thing
// nobody asked for.

const BED_SECONDS = 30;

export function QuickMusic({ brand, models, onOpenInPro, prefill, onUsedPrefill, onError }: TabProps) {
  const [prompt, setPrompt] = useState('');
  const [avoid, setAvoid] = useState('');
  const [madeId, setMadeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waited = useElapsed(startedAt);
  const brandId = brand?.id ?? null;
  const { model, setModel, sel, quote, credits } = useBench('music', models.music, () => ({ seconds: BED_SECONDS }));

  useEffect(() => {
    if (!prefill || (prefill.action !== 'compose_bed' && prefill.action !== 'make_music')) return;
    const p = String(prefill.args.prompt ?? ''); const a = String(prefill.args.avoid ?? '');
    if (p) setPrompt(p);
    if (a) setAvoid(a);
    onUsedPrefill?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const why = !model ? 'Choose a model' : prompt.trim().length < 5 ? 'Describe the bed' : quote && !quote.balance.ok ? `Short by ${quote.balance.shortBy.toLocaleString()} credits` : null;

  const watch = useWatchAsset(madeId, brandId);
  const url = useAssetUrl(watch.asset);

  async function send() {
    if (!model) return;
    setBusy(true); setStartedAt(Date.now()); setMadeId(null); onError(null);
    try {
      const r = await api.generateBed(prompt.trim(), brandId, { model: model.id, ...(avoid.trim() ? { avoid: avoid.trim() } : {}) });
      setMadeId(r.asset.id);
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); setStartedAt(null); }
  }

  return (
    <div className="quick-tab">
      <Stage empty="Your bed lands here — a minute or two to compose, then a moment while the render plane looks at it.">
        {url ? <div className="quick-stage-audio"><span>{prompt}</span><audio src={url} controls autoPlay /></div> : null}
      </Stage>
      <WatchNote asset={watch.asset} stalled={watch.stalled} lookAgain={watch.lookAgain} waiting={`Composing… the model is at work${watch.asset?.duration_ms ? ` · ${saySeconds(Math.round(watch.asset.duration_ms / 1000))}` : ''}`} />

      <ModelPicker bench="music" models={models.music ?? null} value={model} onChange={setModel} />

      <textarea className="quick-prompt" value={prompt} rows={2} maxLength={500} placeholder="Asian folk, bamboo flute, gentle, under dialogue" onChange={(e) => setPrompt(e.target.value)} />
      <input value={avoid} maxLength={200} placeholder="Keep out — drums, vocals" onChange={(e) => setAvoid(e.target.value)} />

      <div className="quick-actions">
        <SendButton label="Compose" busyLabel={`Composing… ${saySeconds(waited)}`} busy={busy} credits={credits} why={why} onClick={() => void send()} />
        <OpenInPro what="The Audio Lab: voices for the cast, and this brief with it." onClick={() => onOpenInPro({
          bench: 'audio', move: 'audio-music',
          prefill: { action: 'compose_bed', say: '', args: { prompt: prompt.trim(), avoid: avoid.trim() }, bench: 'audio', move: 'audio-music', inline: false },
        })} />
      </div>
      <QuoteLine text={credits === null ? null : `${sayQuote(credits, sayWhat('music', model, { ...sel, seconds: BED_SECONDS }), quote?.balance)} Any episode's mix can pick it up.`} />
    </div>
  );
}
